import type { MemberType } from './member';

import { queryToString } from '@shared/helpers/api-utils';
import constructErrorResponse, { ErrorResponseType } from '../errorResponse';
import axios, { AxiosError } from '@shared/helpers/axios';

export const apiPath = 'api/v2/user-notifications';

export interface UserNotificationType {
    id: number;
    acked: boolean;
    details: string;
    imageUrl: string;
    ctaUrl: string;
    carry: Record<string, any>;
    notificationId: number;
    memberId: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface PopulatedUserNotification extends UserNotificationType {
    Member?: MemberType;
    Notification?: Record<string, any>;
}

export type IndexUserNotificationReturnType = {
    data: PopulatedUserNotification[];
    meta: {
        total: number;
    };
};

// interface MutateResponseType {
//   data: UserNotificationType | undefined;
//   error: ErrorResponseType | undefined;
// }

interface MutateResponseArrayType {
    data: UserNotificationType[] | undefined;
    error: ErrorResponseType | undefined;
}

export const getIndexUrl = (queries: Record<string, any>): string => {
    return `${process.env.REACT_APP_API_URL}/${apiPath}/index?${queryToString(queries)}`;
};

export const countNotifByMemberId = async (
    topicKey: string
): Promise<{ data: { total: number }; error?: ErrorResponseType }> => {
    let response: { data: { total: number }; error?: ErrorResponseType } = {
        data: { total: 0 },
        error: undefined,
    };

    try {
        const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/${apiPath}/count-by-current-user?topicKey=${topicKey}`
        );
        response.data = data;
    } catch (error) {
        const axiosError = error as AxiosError;
        let errorMessage = axiosError.response?.data || 'network error';
        let errorStatus = axiosError.response?.status;

        if (typeof errorMessage !== 'string' && errorMessage.error)
            errorMessage = axiosError.response?.data?.error?.cause;

        response.error = constructErrorResponse({
            errorMessage,
            errorStatus,
        });
    }
    return response;
};

export const countNotifByMemberIdWithNotNullAdminId = async (
    topicKey: string
): Promise<{ data: { total: number }; error?: ErrorResponseType }> => {
    let response: { data: { total: number }; error?: ErrorResponseType } = {
        data: { total: 0 },
        error: undefined,
    };

    try {
        const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/${apiPath}/count-by-current-user-with-not-null-admin-id?topicKey=${topicKey}`
        );
        response.data = data;
    } catch (error) {
        const axiosError = error as AxiosError;
        let errorMessage = axiosError.response?.data || 'network error';
        let errorStatus = axiosError.response?.status;

        if (typeof errorMessage !== 'string' && errorMessage.error)
            errorMessage = axiosError.response?.data?.error?.cause;

        response.error = constructErrorResponse({
            errorMessage,
            errorStatus,
        });
    }
    return response;
};

export const removeAll = async (params: {
    memberId?: number;
    topicKey: string;
}): Promise<MutateResponseArrayType> => {
    let response: MutateResponseArrayType = {
        data: undefined,
        error: undefined,
    };

    try {
        const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL}/${apiPath}/remove-all`,
            {
                ...params,
            }
        );

        response.data = data;
    } catch (error) {
        const axiosError = error as AxiosError;

        let errorMessage = axiosError.response?.data || 'network error';
        let errorStatus = axiosError.response?.status;

        if (typeof errorMessage !== 'string' && errorMessage.error)
            errorMessage = axiosError.response?.data?.error?.cause;

        response.error = constructErrorResponse({
            errorMessage,
            errorStatus,
        });
    }
    return response;
};

export const ackAll = async (params: {
    memberId?: number;
    topicKey: string;
}): Promise<MutateResponseArrayType> => {
    let response: MutateResponseArrayType = {
        data: undefined,
        error: undefined,
    };

    try {
        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/${apiPath}/ack-all`, {
            ...params,
        });

        response.data = data;
    } catch (error) {
        const axiosError = error as AxiosError;

        let errorMessage = axiosError.response?.data || 'network error';
        let errorStatus = axiosError.response?.status;

        if (typeof errorMessage !== 'string' && errorMessage.error)
            errorMessage = axiosError.response?.data?.error?.cause;

        response.error = constructErrorResponse({
            errorMessage,
            errorStatus,
        });
    }

    return response;
};

export const ack = async (id: number): Promise<{ data?: UserNotificationType; error?: ErrorResponseType }> => {
    let response: { data?: UserNotificationType; error?: ErrorResponseType } = {
        data: undefined,
        error: undefined,
    };

    try {
        const { data } = await axios.delete(
            `${process.env.REACT_APP_API_URL}/${apiPath}/ack/${id}`
        );
        response.data = data;
    } catch (error) {
        const axiosError = error as AxiosError;
        let errorMessage = axiosError.response?.data || 'network error';
        let errorStatus = axiosError.response?.status;

        if (typeof errorMessage !== 'string' && errorMessage.error)
            errorMessage = axiosError.response?.data?.error?.cause;

        response.error = constructErrorResponse({
            errorMessage,
            errorStatus,
        });
    }

    return response;
};

export interface CountByChatroomsResponse {
    chatroomId: number;
    notifCount: number;
    userNotifIds: number[];
}

export const countByChatrooms = async (
    queryStr: string
): Promise<{ data: CountByChatroomsResponse[]; error?: ErrorResponseType }> => {
    let response: { data: CountByChatroomsResponse[]; error?: ErrorResponseType } = {
        data: [],
        error: undefined,
    };

    try {
        const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/${apiPath}/count-by-chatrooms?${queryStr}`
        );
        response.data = data;
    } catch (error) {
        const axiosError = error as AxiosError;
        let errorMessage = axiosError.response?.data || 'network error';
        let errorStatus = axiosError.response?.status;

        if (typeof errorMessage !== 'string' && errorMessage.error)
            errorMessage = axiosError.response?.data?.error?.cause;

        response.error = constructErrorResponse({
            errorMessage,
            errorStatus,
        });
    }
    return response;
};

export const multipleRemove = async (ids: number[]): Promise<MutateResponseArrayType> => {
    let response: MutateResponseArrayType = {
        data: undefined,
        error: undefined,
    };

    try {
        const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL}/${apiPath}/multiple-remove`,
            { ids }
        );
        response.data = data;
    } catch (error) {
        const axiosError = error as AxiosError;
        let errorMessage = axiosError.response?.data || 'network error';
        let errorStatus = axiosError.response?.status;

        if (typeof errorMessage !== 'string' && errorMessage.error)
            errorMessage = axiosError.response?.data?.error?.cause;

        response.error = constructErrorResponse({
            errorMessage,
            errorStatus,
        });
    }
    return response;
};

export const multipleAck = async (ids: number[]): Promise<MutateResponseArrayType> => {
    let response: MutateResponseArrayType = {
        data: undefined,
        error: undefined,
    };

    try {
        const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL}/${apiPath}/multiple-ack`,
            { ids }
        );
        response.data = data;
    } catch (error) {
        const axiosError = error as AxiosError;
        let errorMessage = axiosError.response?.data || 'network error';
        let errorStatus = axiosError.response?.status;

        if (typeof errorMessage !== 'string' && errorMessage.error)
            errorMessage = axiosError.response?.data?.error?.cause;

        response.error = constructErrorResponse({
            errorMessage,
            errorStatus,
        });
    }
    return response;
};

export const remove = async (
    id: number
): Promise<{ data?: UserNotificationType; error?: ErrorResponseType }> => {
    let response: { data?: UserNotificationType; error?: ErrorResponseType } = {
        data: undefined,
        error: undefined,
    };

    try {
        const { data } = await axios.delete(
            `${process.env.REACT_APP_API_URL}/${apiPath}/remove/${id}`
        );
        response.data = data;
    } catch (error) {
        const axiosError = error as AxiosError;
        let errorMessage = axiosError.response?.data || 'network error';
        let errorStatus = axiosError.response?.status;

        if (typeof errorMessage !== 'string' && errorMessage.error)
            errorMessage = axiosError.response?.data?.error?.cause;

        response.error = constructErrorResponse({
            errorMessage,
            errorStatus,
        });
    }

    return response;
};
